import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Tooltip,
} from '@heroui/react'
import {
  ExternalLink,
  Globe,
  Trash2,
  X,
} from 'lucide-react'
import { useMemo } from 'react'

function DomainGroup({ domain, urls, onRemoveUrl, onRemoveAllFromDomain }) {
  const sortedUrls = useMemo(() => {
    return urls.sort((a, b) => {
      try {
        const aPath = new URL(a).pathname
        const bPath = new URL(b).pathname
        return aPath.localeCompare(bPath)
      }
      catch {
        return a.localeCompare(b)
      }
    })
  }, [urls])

  const formatPath = (url) => {
    try {
      const urlObj = new URL(url)
      const fullPath = urlObj.pathname + urlObj.search
      return fullPath === '/' ? '/' : fullPath
    }
    catch {
      return url
    }
  }

  return (
    <Card className="w-full" radius="none">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold">{domain}</h3>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Chip size="sm" color="primary" variant="flat">
              {urls.length}
              {' '}
              selected
            </Chip>

            <Tooltip content={`Remove all URLs from ${domain}`}>
              <Button
                isIconOnly
                variant="light"
                size="sm"
                onPress={() => onRemoveAllFromDomain(domain)}
                className="text-red-600 hover:bg-red-50"
                radius="none"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </Tooltip>
          </div>
        </div>
      </CardHeader>

      <CardBody className="pt-0">
        <div className="max-h-72 overflow-y-auto space-y-2 pr-2">
          {sortedUrls.map(url => (
            <div
              key={url}
              className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 transition-colors group"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0"></div>

              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm text-gray-900 truncate" title={url}>
                  {formatPath(url)}
                </div>
                <div className="text-xs text-gray-500 truncate" title={url}>
                  {url}
                </div>
              </div>

              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Tooltip content="Open in new tab">
                  <Button
                    isIconOnly
                    variant="light"
                    size="sm"
                    onPress={() => window.open(url, '_blank')}
                    className="text-gray-600 hover:bg-white"
                    radius="none"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                </Tooltip>

                <Tooltip content="Remove URL">
                  <Button
                    isIconOnly
                    variant="light"
                    size="sm"
                    onPress={() => onRemoveUrl(url)}
                    className="text-red-600 hover:bg-red-50"
                    radius="none"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  )
}

export default function URLReviewList({ selectedUrls, onSelectedUrlsChange }) {
  const groupedUrls = useMemo(() => {
    const groups = {}

    selectedUrls.forEach((url) => {
      try {
        const domain = new URL(url).hostname
        if (!groups[domain]) {
          groups[domain] = []
        }
        groups[domain].push(url)
      }
      catch {
        // Invalid URL, add to "other" group
        if (!groups['Invalid URLs']) {
          groups['Invalid URLs'] = []
        }
        groups['Invalid URLs'].push(url)
      }
    })

    return groups
  }, [selectedUrls])

  const sortedDomains = useMemo(() => {
    return Object.keys(groupedUrls).sort()
  }, [groupedUrls])

  const totalUrls = selectedUrls.length

  const handleRemoveUrl = (urlToRemove) => {
    const newUrls = selectedUrls.filter(url => url !== urlToRemove)
    onSelectedUrlsChange(newUrls)
  }

  const handleRemoveAllFromDomain = (domain) => {
    const newUrls = selectedUrls.filter((url) => {
      try {
        return new URL(url).hostname !== domain
      }
      catch {
        return domain !== 'Invalid URLs'
      }
    })
    onSelectedUrlsChange(newUrls)
  }

  if (totalUrls === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Globe className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h3 className="text-lg font-medium mb-2">No URLs Selected</h3>
        <p className="text-sm">Go back and select some URLs to scrape.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Selected URLs</h2>
          <p className="text-sm text-gray-600 mt-1">
            Review and manage the
            {' '}
            {totalUrls}
            {' '}
            URL
            {totalUrls !== 1 ? 's' : ''}
            {' '}
            you've selected for scraping
          </p>
        </div>

        <div className="flex items-center gap-3">
        </div>
      </div>

      {/* Grouped URLs */}
      <div className="space-y-4">
        {sortedDomains.map(domain => (
          <DomainGroup
            key={domain}
            domain={domain}
            urls={groupedUrls[domain]}
            onRemoveUrl={handleRemoveUrl}
            onRemoveAllFromDomain={handleRemoveAllFromDomain}
          />
        ))}
      </div>

      {/* Summary */}
      <div className="bg-white border border-gray-200 p-4">
        <h4 className="font-semibold text-gray-900 mb-3">Scraping Overview</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <span className="text-sm text-gray-700">
              <span className="font-medium">{totalUrls}</span>
              {' '}
              page
              {totalUrls !== 1 ? 's' : ''}
              {' '}
              selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
            <span className="text-sm text-gray-700">
              <span className="font-medium">{sortedDomains.length}</span>
              {' '}
              unique domain
              {sortedDomains.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-600 rounded-full"></div>
            <span className="text-sm text-gray-700">
              <span className="font-medium">
                2
                {' '}
                -
                4
                {' '}
                min
              </span>
              {' '}
              estimated
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span className="text-sm text-gray-700">
              Runs in
              {' '}
              <span className="font-medium">background</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
